<script setup lang="ts">
import { inject, ref, Ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { DateTime } from 'luxon';
import { useConvertQuoteBookingStore } from '@/store/ConvertQuoteBookingStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';

const dialogRef = inject('dialogRef') as Ref;
const qId = dialogRef.value.data.quoteId;
const bookingData = dialogRef.value.data.bookingData;
const isSave = dialogRef.value.options.emits.isSave;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const show = ref(true);
const covertQuoteBookingModule = useConvertQuoteBookingStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;

const convertData = ref(
  cloneDeep({
    ...bookingData,
    depositDate: bookingData.depositDate ? new Date(DateTime.fromISO(bookingData.depositDate)) : null,
    finalPaymentDate: bookingData.finalPaymentDate ? new Date(DateTime.fromISO(bookingData.finalPaymentDate)) : null,
    passengerNameDueDate: bookingData.passengerNameDueDate ? new Date(DateTime.fromISO(bookingData.passengerNameDueDate)) : null
  })
);

const formatDate = (date) => {
  return date ? DateTime.fromISO(new Date(date).toISOString()).toFormat('yyyy-LL-dd') : '';
};

const convertBookingId = () => {
  if (convertData.value.bookingId?.length) {
    const bookIdArray = convertData.value.bookingId.split(',');
    convertData.value.bookingId = bookIdArray;
  } else {
    convertData.value.bookingId = [''];
  }
};
convertBookingId();

const changeBookingToString = (booking: any) => {
  return booking.join(',');
};

const onSave = () => {
  const payload = {
    bookingId: changeBookingToString(convertData.value.bookingId),
    depositDate: formatDate(convertData.value.depositDate),
    finalPaymentDate: formatDate(convertData.value.finalPaymentDate),
    depositAmount: convertData.value.depositAmount,
    finalPayment: convertData.value.finalPayment || 0,
    passengerNameDueDate: formatDate(convertData.value.passengerNameDueDate)
  };
  if (bookingData.bookingId) {
    covertQuoteBookingModule
      .updateBookingById(qId, payload)
      .then(() => {
        toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
        isSave(true);
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
        isSave(false);
      });
  } else {
    covertQuoteBookingModule
      .handleAddBookingId(qId, payload)
      .then(() => {
        toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
        isSave(true);
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
        isSave(false);
      });
  }
};

const addNewBookingId = () => {
  convertData.value.bookingId.push('');
};

const removeBookingId = (index: number) => {
  if (index >= 0 && index < convertData.value.bookingId.length) {
    convertData.value.bookingId.splice(index, 1);
  }
};

const preventComma = (event: any) => {
  if (event.key === ',') {
    event.preventDefault();
  }
};

const removeComma = (index: number) => {
  convertData.value.bookingId[index] = convertData.value.bookingId[index].replace(/,/g, '');
};

const checkEmptyBookingId = computed(() => {
  return convertData.value.bookingId?.some((item: any) => item == '');
});
</script>
<template>
  <div class="convert-quote-booking">
    <div v-if="show" class="modal-mask">
      <div class="modal-container">
        <div class="booking-field">
          <div class="booking-id mt-2" v-for="(bookId, index) in convertData.bookingId" :key="index">
            <div class="book-id-title mb-2 flex justify-content-between align-items-center">
              <label for="bookingId">{{ t('convert_qoute_booking.booking_id') }}</label>
              <div class="action-text" v-if="index">
                <span class="cursor-pointer remove-booking" :id="'remove-' + index" @click="removeBookingId(index)">-{{ t('common.buttons.remove') }}</span>
              </div>
            </div>
            <InputText class="w-full" id="bookingId" @input="removeComma(index)" @keydown="preventComma" maxlength="9" v-model="convertData.bookingId[index]" />
          </div>
          <div class="add-id">
            <div class="action-text flex justify-content-center">
              <span class="cursor-pointer add-booking" @click="addNewBookingId">+{{ t('convert_qoute_booking.add_booking_id') }}</span>
            </div>
          </div>
        </div>
        <div class="booking-field">
          <label for="depositDate">{{ t('convert_qoute_booking.deposit_date') }}</label>
          <DatePicker dateFormat="dd-M-yy" inputId="depositDate" v-model="convertData.depositDate" autocomplete="off" class="p-inputtext-sm w-full" :placeholder="$t('common.date_format')">
            <template #footer>
              <p class="text-center">
                <small>{{ $t('common.press_space') }}</small>
              </p>
            </template>
          </DatePicker>
        </div>
        <div class="booking-field">
          <label for="deposit_amount">{{ t('convert_qoute_booking.deposit_amount') }}</label>
          <p class="text-600 text-xs font-italic mb-0">* {{ $t('validate.min_value') }}</p>
          <InputNumber :min="0" :minFractionDigits="2" id="deposit_amount" type="text" v-model="convertData.depositAmount" />
        </div>
        <div class="booking-field">
          <label for="PaymentDate">{{ t('convert_qoute_booking.final_payment_date') }}</label>
          <DatePicker dateFormat="dd-M-yy" inputId="PaymentDate" v-model="convertData.finalPaymentDate" autocomplete="off" class="p-inputtext-sm w-full" :placeholder="$t('common.date_format')">
            <template #footer>
              <p class="text-center">
                <small>{{ $t('common.press_space') }}</small>
              </p>
            </template>
          </DatePicker>
        </div>

        <div class="booking-field payment_amount">
          <label for="payment_amount">{{ t('convert_qoute_booking.final_payment_amount') }}</label>
          <p class="text-600 text-xs font-italic mb-0">* {{ $t('validate.min_value_label') }}</p>
          <InputNumber :min="0" :minFractionDigits="2" id="payment_amount" type="text" v-model="convertData.finalPayment" />
        </div>

        <div class="booking-field">
          <label for="PassengerNameDate">{{ t('convert_qoute_booking.passenger_name_date') }}</label>
          <DatePicker dateFormat="dd-M-yy" inputId="PassengerNameDate" v-model="convertData.passengerNameDueDate" autocomplete="off" class="p-inputtext-sm w-full" :placeholder="$t('common.date_format')">
            <template #footer>
              <p class="text-center">
                <small>{{ $t('common.press_space') }}</small>
              </p>
            </template>
          </DatePicker>
        </div>
        <div class="flex" v-if="authorizations?.quote_management?.tts_booking_edit">
          <Button
            class="mx-auto submit-btn"
            :label="t('common.buttons.save')"
            :disabled="checkEmptyBookingId || !convertData.depositDate || !convertData.finalPaymentDate || !convertData.depositAmount || !convertData.passengerNameDueDate"
            @click="onSave()"
          ></Button>
        </div>
      </div>
    </div>
  </div>
</template>
